import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Clock, Loader2, ChevronDown, ChevronRight, Users, CalendarSearch, Check, AlertTriangle, XCircle } from 'lucide-react';
import { getAvailableSlots, fetchDaysmartEmployees } from '../services/daysmartService';
import useUserStore from '../stores/userStore';
import { LOCATION_NAME_TO_ID } from '../utils/constants';

// Duration options: 15m to 5hr in 15m increments
const DURATION_OPTIONS = [];
for (let m = 15; m <= 300; m += 15) {
    let label;
    if (m < 60) {
        label = `${m}m`;
    } else if (m % 60 === 0) {
        label = `${m / 60}hr`;
    } else {
        label = `${Math.floor(m / 60)}hr ${m % 60}m`;
    }
    DURATION_OPTIONS.push({ value: m, label });
}

const LOCATION_ID_TO_LABEL = { 1: 'Hopkins', 2: 'Inver Grove' };

/**
 * Get dates for a given week offset from today.
 * weeksOut=0 → today through end of this week (Sunday).
 * weeksOut=1..8 → full Mon-Sun week.
 */
function getWeekDates(weeksOut) {
    const today = new Date();
    today.setHours(12, 0, 0, 0); // noon to avoid TZ edge cases
    const dayOfWeek = today.getDay(); // 0=Sun

    if (weeksOut === 0) {
        const dates = [];
        const current = new Date(today);
        const daysUntilSunday = dayOfWeek === 0 ? 0 : 7 - dayOfWeek;
        for (let i = 0; i <= daysUntilSunday; i++) {
            dates.push(new Date(current));
            current.setDate(current.getDate() + 1);
        }
        return dates;
    }

    // Find next Monday
    const daysUntilNextMonday = dayOfWeek === 0 ? 1 : 8 - dayOfWeek;
    const nextMonday = new Date(today);
    nextMonday.setDate(nextMonday.getDate() + daysUntilNextMonday);

    const weekStart = new Date(nextMonday);
    weekStart.setDate(weekStart.getDate() + (weeksOut - 1) * 7);

    const dates = [];
    for (let i = 0; i < 7; i++) {
        const d = new Date(weekStart);
        d.setDate(d.getDate() + i);
        dates.push(d);
    }
    return dates;
}

function formatDateStr(date) {
    return date.toISOString().split('T')[0];
}

function formatDayLabel(date) {
    return date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
}

function formatTime(isoStr) {
    const d = new Date(isoStr);
    return d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
}

const BASE_WEEK_SECTIONS = [
    { key: 'thisWeek', label: 'This Week / Soonest Available', weeksOut: 0 },
    { key: 'week1', label: '1 Week Out', weeksOut: 1 },
    { key: 'week2', label: '2 Weeks Out', weeksOut: 2 },
    { key: 'week3', label: '3 Weeks Out', weeksOut: 3 },
    { key: 'week4', label: '4 Weeks Out', weeksOut: 4 },
    { key: 'week5', label: '5 Weeks Out', weeksOut: 5 },
    { key: 'week6', label: '6 Weeks Out', weeksOut: 6 },
    { key: 'week7', label: '7 Weeks Out', weeksOut: 7 },
    { key: 'week8', label: '8 Weeks Out', weeksOut: 8 },
];

function buildWeekSections(maxWeeksOut) {
    const sections = [...BASE_WEEK_SECTIONS];
    for (let w = 9; w <= maxWeeksOut; w++) {
        sections.push({ key: `week${w}`, label: `${w} Weeks Out`, weeksOut: w });
    }
    return sections;
}

/**
 * Props:
 *   onSlotSelect     - If provided, enables "picker mode": slots become clickable
 *                       and the callback fires with { date, start_time, end_time, employee_id, employee_name }.
 *   defaultLocationId - Override the location derived from the user store (1=Hopkins, 2=Inver Grove).
 */
const AvailabilityPanel = ({ onSlotSelect, defaultLocationId, defaultDuration }) => {
    const { selectedLocation } = useUserStore();

    // Map timekeeping location to DaySmart location
    const effectiveLocation = defaultLocationId
        || (selectedLocation === LOCATION_NAME_TO_ID.Hopkins ? 1
            : selectedLocation === LOCATION_NAME_TO_ID.InverGrove ? 2
            : 1);

    const [locationId, setLocationId] = useState(effectiveLocation);
    const [selectedSlot, setSelectedSlot] = useState(null);
    const [duration, setDuration] = useState(defaultDuration || 120);
    const [employees, setEmployees] = useState([]);
    const [selectedEmployeeIds, setSelectedEmployeeIds] = useState(new Set());
    const [employeesLoading, setEmployeesLoading] = useState(false);
    const [maxWeeksOut, setMaxWeeksOut] = useState(8);
    const weekSections = React.useMemo(() => buildWeekSections(maxWeeksOut), [maxWeeksOut]);

    // Per-section state: { [sectionKey]: { loading, loaded, error, searchingDate, days: [{ date, dateLabel, slots }] } }
    const [sectionData, setSectionData] = useState({});
    const [expandedSections, setExpandedSections] = useState({ thisWeek: true });

    const isPickerMode = typeof onSlotSelect === 'function';
    const cancelSearchAllRef = useRef(false);
    const [searchingAll, setSearchingAll] = useState(false);

    const handleSlotClick = (slot) => {
        if (!isPickerMode) return;
        setSelectedSlot(slot);
        onSlotSelect(slot);
    };

    const isSlotSelected = (slot) => {
        if (!selectedSlot) return false;
        return slot.start_time === selectedSlot.start_time
            && slot.employee_id === selectedSlot.employee_id
            && slot.date === selectedSlot.date;
    };

    // Load employees when location changes
    useEffect(() => {
        let cancelled = false;
        const load = async () => {
            setEmployeesLoading(true);
            try {
                const result = await fetchDaysmartEmployees(locationId);
                if (!cancelled) {
                    const emps = result.employees || [];
                    setEmployees(emps);
                    // Auto-select all employees
                    setSelectedEmployeeIds(new Set(emps.map(e => e.employee_id)));
                }
            } catch (err) {
                console.error('Failed to load employees:', err);
                if (!cancelled) setEmployees([]);
            } finally {
                if (!cancelled) setEmployeesLoading(false);
            }
        };
        load();
        // Reset section data on location change
        setSectionData({});
        return () => { cancelled = true; };
    }, [locationId]);

    // Update locationId if timekeeping location changes
    useEffect(() => {
        setLocationId(effectiveLocation);
    }, [effectiveLocation]);

    // Update duration if defaultDuration prop changes (e.g., history loaded)
    useEffect(() => {
        if (defaultDuration) setDuration(defaultDuration);
    }, [defaultDuration]);

    const toggleEmployee = (empId) => {
        setSelectedEmployeeIds(prev => {
            const next = new Set(prev);
            if (next.has(empId)) next.delete(empId);
            else next.add(empId);
            return next;
        });
    };

    const selectAllEmployees = () => {
        setSelectedEmployeeIds(new Set(employees.map(e => e.employee_id)));
    };

    const deselectAllEmployees = () => {
        setSelectedEmployeeIds(new Set());
    };

    const toggleSection = (sectionKey) => {
        setExpandedSections(prev => ({ ...prev, [sectionKey]: !prev[sectionKey] }));
    };

    /**
     * Search a single week section for availability.
     * For each day, calls the API for each selected employee (or null for all if none selected).
     */
    const searchSection = useCallback(async (section) => {
        if (selectedEmployeeIds.size === 0) return;

        const sectionKey = section.key;
        const dates = getWeekDates(section.weeksOut);
        const empIds = [...selectedEmployeeIds];

        // Mark loading
        setSectionData(prev => ({
            ...prev,
            [sectionKey]: { loading: true, loaded: false, error: null, searchingDate: null, days: [] }
        }));

        // Auto-expand
        setExpandedSections(prev => ({ ...prev, [sectionKey]: true }));

        const days = [];

        try {
            for (const date of dates) {
                // Check for cancellation before each day
                if (cancelSearchAllRef.current) {
                    setSectionData(prev => ({
                        ...prev,
                        [sectionKey]: { ...prev[sectionKey], loading: false, loaded: true, searchingDate: null }
                    }));
                    return;
                }

                const dateStr = formatDateStr(date);

                // Update searching indicator
                setSectionData(prev => ({
                    ...prev,
                    [sectionKey]: { ...prev[sectionKey], searchingDate: dateStr }
                }));

                // Fetch slots for all selected employees in parallel for this day
                const promises = empIds.map(empId => {
                    const emp = employees.find(e => e.employee_id === empId);
                    return getAvailableSlots(
                        locationId,
                        dateStr,
                        empId,
                        emp?.name || null,
                        duration,
                        true // exact_date
                    ).then(result => {
                        return (result.available_slots || []).filter(s => {
                            const slotDate = s.date || (s.start_time ? s.start_time.slice(0, 10) : '');
                            return slotDate === dateStr;
                        });
                    }).catch(err => {
                        console.warn(`Failed to fetch slots for emp ${empId} on ${dateStr}:`, err);
                        return [];
                    });
                });

                const results = await Promise.all(promises);
                const daySlots = results.flat();

                // Sort by start time, then employee name
                daySlots.sort((a, b) => {
                    const timeCmp = (a.start_time || '').localeCompare(b.start_time || '');
                    if (timeCmp !== 0) return timeCmp;
                    return (a.employee_name || '').localeCompare(b.employee_name || '');
                });

                days.push({
                    date: dateStr,
                    dateLabel: formatDayLabel(date),
                    slots: daySlots,
                });

                // Update partial results as we go
                setSectionData(prev => ({
                    ...prev,
                    [sectionKey]: { ...prev[sectionKey], days: [...days] }
                }));
            }

            setSectionData(prev => ({
                ...prev,
                [sectionKey]: { ...prev[sectionKey], loading: false, loaded: true, searchingDate: null }
            }));
        } catch (err) {
            setSectionData(prev => ({
                ...prev,
                [sectionKey]: { ...prev[sectionKey], loading: false, error: err.message, searchingDate: null }
            }));
        }
    }, [locationId, duration, selectedEmployeeIds, employees]);

    const searchAllSections = useCallback(async () => {
        cancelSearchAllRef.current = false;
        setSearchingAll(true);
        try {
            for (const section of weekSections) {
                if (cancelSearchAllRef.current) break;
                await searchSection(section);
            }
        } finally {
            setSearchingAll(false);
        }
    }, [searchSection, weekSections]);

    const cancelSearchAll = useCallback(() => {
        cancelSearchAllRef.current = true;
    }, []);

    const locationLabel = LOCATION_ID_TO_LABEL[locationId] || 'Unknown';

    return (
        <div className="space-y-4">
            {/* Controls Card */}
            <div className="bg-white rounded-lg shadow p-4">
                <div className="flex items-center gap-2 mb-4">
                    <CalendarSearch className="text-purple-600" size={22} />
                    <h2 className="text-lg font-semibold text-gray-800">Groomer Availability Search</h2>
                    <span className="ml-auto text-sm text-gray-500 bg-gray-100 px-2 py-0.5 rounded">
                        {locationLabel}
                    </span>
                </div>

                {/* Location + Duration row */}
                <div className="flex flex-wrap items-end gap-4 mb-4">
                    <div>
                        <label className="text-xs font-medium text-gray-600 block mb-1">Location</label>
                        <select
                            value={locationId}
                            onChange={(e) => setLocationId(parseInt(e.target.value))}
                            className="border rounded px-3 py-2 text-sm focus:ring-purple-500 focus:border-purple-500"
                        >
                            <option value={1}>Hopkins</option>
                            <option value={2}>Inver Grove</option>
                        </select>
                    </div>
                    <div>
                        <label className="text-xs font-medium text-gray-600 block mb-1">Appointment Duration</label>
                        <select
                            value={duration}
                            onChange={(e) => setDuration(parseInt(e.target.value))}
                            className="border rounded px-3 py-2 text-sm focus:ring-purple-500 focus:border-purple-500"
                        >
                            {DURATION_OPTIONS.map(opt => (
                                <option key={opt.value} value={opt.value}>{opt.label}</option>
                            ))}
                        </select>
                    </div>
                    <button
                        onClick={() => searchSection(weekSections[0])}
                        disabled={selectedEmployeeIds.size === 0 || employeesLoading}
                        className="px-4 py-2 bg-purple-600 text-white rounded text-sm font-medium hover:bg-purple-700 transition-colors disabled:opacity-50 flex items-center gap-2"
                    >
                        <CalendarSearch size={16} />
                        Search This Week
                    </button>
                    {searchingAll ? (
                        <button
                            onClick={cancelSearchAll}
                            className="px-4 py-2 bg-red-600 text-white rounded text-sm font-medium hover:bg-red-700 transition-colors flex items-center gap-2"
                        >
                            <XCircle size={16} />
                            Cancel Search
                        </button>
                    ) : (
                        <button
                            onClick={searchAllSections}
                            disabled={selectedEmployeeIds.size === 0 || employeesLoading}
                            className="px-4 py-2 bg-green-600 text-white rounded text-sm font-medium hover:bg-green-700 transition-colors disabled:opacity-50 flex items-center gap-2"
                        >
                            <CalendarSearch size={16} />
                            Search All Weeks
                        </button>
                    )}
                </div>

                {/* Employee Selection */}
                <div className="border rounded-lg p-3 bg-gray-50">
                    <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                            <Users size={16} className="text-gray-600" />
                            <span className="text-sm font-medium text-gray-700">
                                Groomers ({selectedEmployeeIds.size}/{employees.length} selected)
                            </span>
                        </div>
                        <div className="flex gap-2">
                            <button
                                onClick={selectAllEmployees}
                                className="text-xs text-purple-600 hover:text-purple-800 font-medium"
                            >
                                Select All
                            </button>
                            <span className="text-gray-300">|</span>
                            <button
                                onClick={deselectAllEmployees}
                                className="text-xs text-purple-600 hover:text-purple-800 font-medium"
                            >
                                Clear
                            </button>
                        </div>
                    </div>

                    {employeesLoading ? (
                        <div className="flex items-center gap-2 py-2 text-gray-500">
                            <Loader2 className="animate-spin" size={14} />
                            <span className="text-sm">Loading groomers…</span>
                        </div>
                    ) : (
                        <div className="flex flex-wrap gap-2">
                            {employees.map(emp => {
                                const isSelected = selectedEmployeeIds.has(emp.employee_id);
                                return (
                                    <button
                                        key={emp.employee_id}
                                        onClick={() => toggleEmployee(emp.employee_id)}
                                        className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors flex items-center gap-1 ${
                                            isSelected
                                                ? 'bg-purple-100 border-purple-300 text-purple-700'
                                                : 'bg-white border-gray-200 text-gray-500 hover:border-gray-300'
                                        }`}
                                    >
                                        {isSelected && <Check size={12} />}
                                        {emp.name}
                                    </button>
                                );
                            })}
                            {employees.length === 0 && (
                                <span className="text-xs text-gray-400 italic">No groomers found for this location</span>
                            )}
                        </div>
                    )}
                </div>
            </div>

            {/* Week Sections */}
            {weekSections.map((section) => {
                const data = sectionData[section.key] || {};
                const isExpanded = expandedSections[section.key] || false;
                const dates = getWeekDates(section.weeksOut);
                const dateRange = `${formatDayLabel(dates[0])} – ${formatDayLabel(dates[dates.length - 1])}`;
                const totalSlots = (data.days || []).reduce((sum, d) => sum + d.slots.length, 0);

                return (
                    <div key={section.key} className="bg-white rounded-lg shadow overflow-hidden">
                        {/* Section Header */}
                        <button
                            onClick={() => toggleSection(section.key)}
                            className="w-full flex items-center justify-between px-4 py-3 hover:bg-gray-50 transition-colors"
                        >
                            <div className="flex items-center gap-2">
                                {isExpanded ? <ChevronDown size={18} className="text-gray-400" /> : <ChevronRight size={18} className="text-gray-400" />}
                                <span className="font-medium text-gray-800">{section.label}</span>
                                <span className="text-xs text-gray-400">{dateRange}</span>
                            </div>
                            <div className="flex items-center gap-3">
                                {data.loading && (
                                    <div className="flex items-center gap-1 text-purple-500">
                                        <Loader2 className="animate-spin" size={14} />
                                        <span className="text-xs">
                                            {data.searchingDate
                                                ? formatDayLabel(new Date(data.searchingDate + 'T12:00:00'))
                                                : 'Searching…'}
                                        </span>
                                    </div>
                                )}
                                {data.loaded && !data.loading && (
                                    <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                                        totalSlots > 0
                                            ? 'bg-green-100 text-green-700'
                                            : 'bg-gray-100 text-gray-500'
                                    }`}>
                                        {totalSlots} slot{totalSlots !== 1 ? 's' : ''} found
                                    </span>
                                )}
                                {!data.loaded && !data.loading && (
                                    <button
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            searchSection(section);
                                        }}
                                        disabled={selectedEmployeeIds.size === 0}
                                        className="text-xs px-3 py-1 bg-purple-100 text-purple-700 rounded font-medium hover:bg-purple-200 transition-colors disabled:opacity-50"
                                    >
                                        Search
                                    </button>
                                )}
                            </div>
                        </button>

                        {/* Section Content */}
                        {isExpanded && (
                            <div className="border-t">
                                {data.error && (
                                    <div className="px-4 py-3 bg-yellow-50 flex items-center gap-2 text-yellow-700 text-sm">
                                        <AlertTriangle size={14} />
                                        {data.error}
                                    </div>
                                )}

                                {!data.loaded && !data.loading && !data.error && (
                                    <div className="px-4 py-6 text-center text-sm text-gray-400">
                                        Click <span className="font-medium text-purple-600">Search</span> to check availability for this week
                                    </div>
                                )}

                                {(data.days || []).map((day) => (
                                    <div key={day.date} className="border-b last:border-b-0">
                                        <div className="px-4 py-2 bg-gray-50 flex items-center justify-between">
                                            <span className="text-sm font-medium text-gray-700">{day.dateLabel}</span>
                                            <span className={`text-xs ${day.slots.length > 0 ? 'text-green-600' : 'text-gray-400'}`}>
                                                {day.slots.length > 0
                                                    ? `${day.slots.length} opening${day.slots.length !== 1 ? 's' : ''}`
                                                    : 'No openings'}
                                            </span>
                                        </div>
                                        {day.slots.length > 0 && (
                                            <div className="px-4 py-2 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                                                {day.slots.map((slot, idx) => {
                                                    const selected = isPickerMode && isSlotSelected(slot);
                                                    return (
                                                        <div
                                                            key={idx}
                                                            onClick={() => handleSlotClick(slot)}
                                                            className={`text-xs p-2 rounded border transition-colors ${
                                                                selected
                                                                    ? 'border-purple-400 bg-purple-100 ring-2 ring-purple-300'
                                                                    : 'border-green-200 bg-green-50 hover:bg-green-100'
                                                            } ${isPickerMode ? 'cursor-pointer' : ''}`}
                                                        >
                                                            <div className="flex items-center justify-between">
                                                                <span className={`font-medium flex items-center gap-1 ${selected ? 'text-purple-800' : 'text-green-800'}`}>
                                                                    <Clock size={11} />
                                                                    {formatTime(slot.start_time)} – {formatTime(slot.end_time)}
                                                                </span>
                                                                {selected && <Check size={12} className="text-purple-600" />}
                                                            </div>
                                                            {slot.employee_name && (
                                                                <div className={`mt-0.5 ${selected ? 'text-purple-600' : 'text-green-600'}`}>
                                                                    {slot.employee_name}
                                                                </div>
                                                            )}
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        )}
                                    </div>
                                ))}

                                {data.loading && (data.days || []).length === 0 && (
                                    <div className="px-4 py-6 flex items-center justify-center gap-2 text-purple-500">
                                        <Loader2 className="animate-spin" size={16} />
                                        <span className="text-sm">Searching availability…</span>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                );
            })}

            {/* See further out button */}
            <button
                onClick={() => setMaxWeeksOut(prev => prev + 4)}
                className="w-full py-3 bg-white rounded-lg shadow text-sm font-medium text-purple-600 hover:bg-purple-50 hover:text-purple-700 transition-colors"
            >
                See further out →
            </button>
        </div>
    );
};

export default AvailabilityPanel;
